import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api/client';
import { getApiErrorMessage } from '@/lib/utils';

interface DeleteDialogProps {
  id: number | string;
  title: string;
  url: string;
  queryKey: string | readonly unknown[];
  variant?: 'side' | 'btn';
  setOpenEvent?: (open: boolean) => void;
  onSuccess?: (id: number | string) => void;
}

export function DeleteDialog({
  id,
  title,
  url,
  queryKey,
  variant = 'side',
  setOpenEvent,
  onSuccess,
}: DeleteDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const deleteData = async (deleteId: number | string) => {
    return api.delete(`/${url}/${deleteId}`);
  };

  const mutation = useMutation({
    mutationFn: deleteData,
    onSuccess: () => {
      toast.success(t('toast.deletedSuccess', { item: title }));
      queryClient.invalidateQueries({ queryKey: typeof queryKey === 'string' ? [queryKey] : queryKey });
      if (typeof setOpenEvent === 'function') {
        setOpenEvent(false);
      }
      if (typeof onSuccess === 'function') {
        onSuccess(id);
      }
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('toast.errorDeleting', { item: title })));
    },
  });

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        {variant === 'side' ? (
          <Button variant="outline" size="sm" className="text-red-600 rounded-l-none">
            <Trash />
          </Button>
        ) : variant === 'btn' ? (
          <Button variant="outline" size="sm" className="text-red-600">
            <Trash />
          </Button>
        ) : null}
      </AlertDialogTrigger>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('areYouSure')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('thisWillPermanentlyDelete')} <strong>{title}</strong>.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={(e) => {
              e.stopPropagation();
              mutation.mutate(id);
            }}
            disabled={mutation.isPending}
          >
            {t('delete')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
